/* eslint-disable react/prop-types */
import React, {forwardRef, useEffect, useMemo, useState} from 'react';
import {
    Box,
    Button,
    Checkbox, Divider,
    FormControl,
    FormControlLabel,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import PaginatedAutocomplete from '../../../../shared-components/PaginatedAutocomplete';
import Dropdown from '../FormBuilder/Components/Dropdown';
import SingleChoice from '../FormBuilder/Components/SingleChoice';
import CreateFormTemplatePreviewList from './CreateFormTemplatePreviewList';
import TriggerFormSettings, {sanitizeTriggerRecipientFieldIds} from '../PrivateFormSettings/TriggerFormSettings';
import CircleIcon from '@mui/icons-material/Circle';
import FORM_AUDIENCE_TYPE from '../../../../constants/FormAudienceType';
import {FORM_ACCESS_LEVELS} from '../../../../constants/formAccessLevels';
import {useGetFormTemplatesQuery} from '../../../../api/services/formsService';
import {useLazyGetAllEmployeeProfilesQuery} from '../../../../api/services/employeeService';
import {useLazyGetStudentFiltersQuery} from '../../../../api/services/studentService';
import EmailsInput from '../../../../shared-components/EmailsInput';
import moment from 'moment';
import {DateTimePicker} from '@mui/x-date-pickers';
import FormExpirationDatePicker from '../FormExpirationDatePicker';
import AdmicityTableV2 from '../../../../shared-components/Table/V2/AdmicityTableV2';
import AddStudentGroupDialog from '../../Forms/PrivateFormSettings/AddStudentGroupDialog';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {useGetGroupFiltersQuery} from '../../../../api/services/groupService';
import FORM_ASSIGNMENT_TYPE from '../../../../constants/FormAssignmentType';
import {useGetCurrentSchoolQuery} from '../../../../api/services/schoolManagementService';
import {GROUP_MEMBERS_TYPE} from '../../../../constants/GroupMembersType';
import FormAccessSettings from '../../Forms/FormAccessSettings';
import ApprovalSettings from '../../Forms/PrivateFormSettings/ApprovalSettings';
import AutoresponderSettings from '../../Forms/PrivateFormSettings/AutoresponderSettings';
import ContentCopyRoundedIcon from '@mui/icons-material/ContentCopyRounded';
import useNotification from '../../../../utility/hooks/useNotification';
import {validateFieldLength} from '../../../../utility/validationUtil';
import {getFormUrl, getIFrame} from '../PublicFormSettings';
import FormEditor from '../FormEditor';
import {useNavigate} from 'react-router-dom';
import RecipientsPreviewDialog from './RecipientsPreviewDialog';
import FORM_TYPE, {
    FORM_TYPE_OPTIONS,
    getFormBehaviorFromType,
    getFormTypeTemplateFilters
} from '../../../../constants/FormType';
import {
    PARENT_AUDIENCE_TYPE,
    getParentAudienceTypeForSelection,
} from '../../../../constants/ParentAudienceType';
import {
    LINKED_EMPLOYEE_SUBMISSION_OPTIONS,
    LINKED_EMPLOYEE_SUBMISSION_QUESTION,
} from '../linkedEmployeeSubmissionMode';

const emptyWizardRecipients = {
    assignmentType: null,
    student: {
        students: [],
        applications: [],
        enquiries: [],
    },
    employees: [],
    groups: [],
    externalEmails: [],
};

const emptyWizardAssociations = {
    students: [],
    applications: [],
    enquiries: [],
    employees: [],
    groups: []
};

const emptyWizardTrigger = {
    selectedForm: null,
    selectedFormSchema: null,
    relatedComponent: null,
    targetState: null,
    relatedFormEmailFieldId: null,
    relatedFormEmployeeFieldId: null,
    externalEmails: [],
};

export const StepFormType = ({
                                 wizard,
                                 setWizard
                             }) => {
    const {data: templates = []} = useGetFormTemplatesQuery();

    const handleChange = ({radioValue}) => {
        const {typeFilter, categoryFilter} = getFormTypeTemplateFilters(radioValue);
        const {
            allowMultipleSubmissions,
            enableRecipientNotifications
        } = getFormBehaviorFromType(radioValue);
        const isTriggerForm = radioValue === FORM_TYPE.triggerForm;

        setWizard(prev => {
            const updated = {
                ...prev,
                form: {
                    ...prev.form,
                    type: radioValue
                },
                template: {
                    ...prev.template,
                    typeFilter,
                    categoryFilter,
                },
                details: {
                    ...prev.details,
                    allowMultipleSubmissions,
                    enableRecipientNotifications,
                    strictRecipientsFiltering: isTriggerForm
                        ? false
                        : prev.details.strictRecipientsFiltering,
                    allLinkedEmployeesCanSubmit: isTriggerForm
                        ? false
                        : prev.details.allLinkedEmployeesCanSubmit,
                    linkedStrictFiltering: isTriggerForm
                        ? false
                        : prev.details.linkedStrictFiltering,
                }
            };

            if (isTriggerForm) {
                updated.recipients = {
                    ...emptyWizardRecipients
                };
                updated.associations = {
                    ...emptyWizardAssociations
                };
                updated.trigger = {
                    ...emptyWizardTrigger
                };
            } else {
                updated.trigger = {
                    ...emptyWizardTrigger
                };
            }

            if (radioValue === FORM_TYPE.shareable) {
                const shareableTemplate = templates.find(t => t.type === 'shareable');
                if (shareableTemplate) {
                    updated.template = {
                        ...updated.template,
                        templateId: shareableTemplate.id,
                        templateObject: shareableTemplate,
                    };
                }
            }

            return updated;
        });
    };

    return (
        <Box sx={{p: 3}}>
            <Dropdown
                componentId="formType"
                descriptionHtml="<div style='margin-bottom:16px;'><strong>What type of form is this?</strong></div>"
                value={wizard.form.type || ''}
                readonly={true}
                placeholderValues={{}}
                options={FORM_TYPE_OPTIONS}
                isEditMode={false}
                onChange={handleChange}
            />
        </Box>
    );
};

export const StepTriggerConditions = ({
                                          wizard,
                                          setWizard,
                                      }) => {
    const handleSettingsChange = (updater) => {
        setWizard(prev => {
            const updated =
                typeof updater === 'function'
                    ? updater(prev.trigger)
                    : {...prev.trigger, ...updater};

            return {
                ...prev,
                trigger: updated,
            };
        });
    };

    const isTriggerExternalAudience =
        wizard.form.type === 'triggerForm' &&
        wizard.form.audience === FORM_AUDIENCE_TYPE.externalUsers;

    const externalEmails = wizard.trigger.externalEmails ?? [];
    const hasEmailFieldSelected = !!wizard.trigger.relatedFormEmailFieldId;

    const handleExternalEmailsChange = (newEmails) => {
        const deduped = (newEmails || []).filter(
            (v, i, self) =>
                i === self.findIndex(
                    x => x.email?.toLowerCase() === v.email?.toLowerCase()
                )
        );

        setWizard(prev => ({
            ...prev,
            trigger: {
                ...prev.trigger,
                externalEmails: deduped
            }
        }));
    };

    const handleExternalEmailBlur = (email) => {
        if (!email) return;

        const updated = [
            ...(externalEmails || []),
            {
                email,
                isNew: true
            }
        ].filter(
            (v, i, self) =>
                i === self.findIndex(x => x.email === v.email)
        );

        setWizard(prev => ({
            ...prev,
            trigger: {
                ...prev.trigger,
                externalEmails: updated
            }
        }));
    };

    return (
        <Box sx={{p: 3}}>
            <Typography fontWeight="600" variant="body1" color="text.primary" my={2}>
                Which form, question, and answer should trigger sending this form to the recipient?
            </Typography>

            <TriggerFormSettings
                key={`${wizard.form.type || 'none'}-${wizard.form.audience || 'none'}-${wizard.details.linkToStudents ? 'ls' : ''}-${wizard.details.linkToEmployees ? 'le' : ''}`}
                wizardMode={true}
                formId={0}
                audience={wizard.form.audience || 0}
                linkToStudents={wizard.details.linkToStudents}
                linkToEmployees={wizard.details.linkToEmployees}
                settings={wizard.trigger}
                externalUsers={wizard.trigger.externalEmails || []}
                onSettingsChange={handleSettingsChange}
            />

            {isTriggerExternalAudience && (
                <Box mt={4}>
                    <Typography fontWeight="600" variant="body1" color="text.primary" mb={1}>
                        External recipients (optional)
                    </Typography>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {hasEmailFieldSelected
                            ? 'Recipients are taken from the selected email field above. Clear "Email from field" if you want to enter addresses manually.'
                            : 'Add external recipient email addresses (optional). Leave empty to use only the email field on the triggering form.'}
                    </Typography>

                    <EmailsInput
                        values={externalEmails.filter(x => !x.isDeleted)}
                        onChange={handleExternalEmailsChange}
                        onBlur={handleExternalEmailBlur}
                        disabled={hasEmailFieldSelected}
                    />
                </Box>
            )}
        </Box>
    );
};

export const StepAudience = ({
                                 wizard,
                                 setWizard
                             }) => {

    const {form} = wizard;
    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: false});

    useEffect(() => {
        const shouldForceLinkedPublicAccess =
            form.audience === FORM_AUDIENCE_TYPE.externalUsers ||
            form.audience === FORM_AUDIENCE_TYPE.enquiries;

        if (!shouldForceLinkedPublicAccess) {
            return;
        }

        if (wizard.details.accessLevel !== FORM_ACCESS_LEVELS.publicWithStudentLink) {
            setWizard(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    accessLevel: FORM_ACCESS_LEVELS.publicWithStudentLink
                }
            }));
        }
    }, [form.audience, wizard.details.accessLevel, setWizard]);

    const dropdownOptions = [
        {
            id: `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|with-account`,
            description: 'Parent with an Admicity account'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|without-account`,
            description: 'Parent without an Admicity account'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.parentsForChildCompletion}|applicant-status`,
            description: 'Parent of applicant with Applied or Offered status'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.previousSchool}`,
            description: 'Child’s previous school'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.employees}`,
            description: 'Employee'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.employees}|linked`,
            description: 'Employee linked to student'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.employees}|linked-employee`,
            description: 'Employee linked to employee'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.externalUsers}`,
            description: 'External recipient'
        },
        {
            id: `${FORM_AUDIENCE_TYPE.students}`,
            description: 'Student'
        },
        ...(schoolInfo && schoolInfo.disableEnquiriesSync === false
            ? [{
                id: `${FORM_AUDIENCE_TYPE.enquiries}`,
                description: 'Enquiry'
            }]
            : [])
    ];
    const audienceDescription = wizard.details.isCommunication
        ? '<div style=\'margin-bottom:16px;\'><strong>Who will receive this email?</strong></div>'
        : '<div style=\'margin-bottom:16px;\'><strong>Who will complete this form?</strong></div>';

    let filteredOptions = dropdownOptions;

    if (form.type === 'selfService') {
        filteredOptions = dropdownOptions.filter(o => {
            const [audId, optionType] = o.id.split('|');
            const numeric = Number(audId);
            return (
                numeric === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                (
                    numeric === FORM_AUDIENCE_TYPE.employees &&
                    optionType !== 'linked' &&
                    optionType !== 'linked-employee'
                )
            );
        });
    }

    if (form.type === 'triggerForm') {
        filteredOptions = filteredOptions.filter(
            o => o.id !== `${FORM_AUDIENCE_TYPE.students}`
        );
    }

    return (
        <Box sx={{p: 3}}>
            <Dropdown
                componentId="audience"
                descriptionHtml={audienceDescription}
                value={form.selection ?? String(form.audience ?? '')}
                options={filteredOptions}
                isEditMode={false}
                readonly={true}
                placeholderValues={{}}
                onChange={({radioValue}) => {
                    const [audienceId, optionType] = String(radioValue).split('|');
                    const numericAudience = Number(audienceId);
                    const isLinkedToStudentOption =
                        numericAudience === FORM_AUDIENCE_TYPE.employees && optionType === 'linked';
                    const isLinkedToEmployeeOption =
                        numericAudience === FORM_AUDIENCE_TYPE.employees && optionType === 'linked-employee';
                    const isLinkedOption = isLinkedToStudentOption || isLinkedToEmployeeOption;
                    const parentAccountType = numericAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion
                        ? (optionType || null)
                        : null;

                    setWizard(prev => ({
                        ...prev,
                        form: {
                            ...prev.form,
                            selection: radioValue,
                            audience: numericAudience,
                        },
                        trigger: sanitizeTriggerRecipientFieldIds(
                            numericAudience,
                            prev.trigger ?? emptyWizardTrigger
                        ),
                        associations: isLinkedOption
                            ? prev.associations
                            : {
                                students: [],
                                applications: [],
                                enquiries: [],
                                employees: [],
                                groups: []
                            },
                        details: {
                            ...prev.details,
                            parentAccountType,
                            linkToStudents: isLinkedToStudentOption,
                            linkToEmployees: isLinkedToEmployeeOption,
                            allLinkedEmployeesCanSubmit: isLinkedOption
                                ? prev.details.allLinkedEmployeesCanSubmit
                                : false,
                            linkedStrictFiltering: isLinkedOption
                                ? prev.details.linkedStrictFiltering
                                : false,
                        }
                    }));
                }}
            />
        </Box>
    );
};

export const StepExternalRecipients = ({
                                           wizard,
                                           setWizard
                                       }) => {
    const {
        form,
        recipients
    } = wizard;

    if (form.audience !== FORM_AUDIENCE_TYPE.externalUsers) return null;

    const emails = recipients.externalEmails ?? [];

    const handleEmailsChange = (newEmails) => {
        const deduped = (newEmails || []).filter(
            (v, i, self) =>
                i === self.findIndex(x => x.email?.toLowerCase() === v.email?.toLowerCase())
        );

        setWizard(prev => ({
            ...prev,
            recipients: {
                ...prev.recipients,
                externalEmails: deduped
            }
        }));
    };

    const handleEmailBlur = (email) => {
        if (!email) return;

        const updated = [
            ...(emails || []),
            {
                email,
                isNew: true
            }
        ].filter(
            (v, i, self) =>
                i === self.findIndex(x => x.email === v.email)
        );

        setWizard(prev => ({
            ...prev,
            recipients: {
                ...prev.recipients,
                externalEmails: updated
            }
        }));
    };

    return (
        <>
            <Box sx={{p: 3}}>
                <Typography fontWeight="600" variant="body1" color="text.primary" my={2}>
                    Add external recipients (optional)
                </Typography>

                <EmailsInput
                    values={emails.filter(x => !x.isDeleted)}
                    onChange={handleEmailsChange}
                    onBlur={handleEmailBlur}
                />
            </Box>
        </>
    );
};

export const StepRecipientAssociation = ({
                                             wizard,
                                             setWizard,
                                             wizardAccessLevel = null
                                         }) => {
    const {
        form,
        recipients,
        details
    } = wizard;

    if (form.type === 'triggerForm') return null;

    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const {data: groupModules} = useGetGroupFiltersQuery({}, {skip: false});
    const groupFilters = recipients.groups || [];
    const assignmentType = recipients.assignmentType ?? FORM_ASSIGNMENT_TYPE.none;
    const activeGroupCount = groupFilters.length;

    const clearRecipientFields = (prev) => ({
        ...prev,
        recipients: {
            ...prev.recipients,
            student: {
                students: [],
                applications: [],
                enquiries: [],
            },
            employees: [],
            groups: [],
            assignmentType: FORM_ASSIGNMENT_TYPE.none,
        }
    });

    const syncArray = (newValue, oldValue = []) => {
        if (!Array.isArray(newValue)) return [];

        const map = new Map();

        newValue.forEach(item => {
            if (item?.id != null) map.set(item.id, item);
        });

        oldValue.forEach(item => {
            if (map.has(item.id) && item.assignmentId) {
                map.set(item.id, {
                    ...map.get(item.id),
                    assignmentId: item.assignmentId
                });
            }
        });

        return [...map.values()];
    };

    const handleRemoveGroup = (id) => {
        setWizard(prev => ({
            ...prev,
            recipients: {
                ...prev.recipients,
                groups: prev.recipients.groups.filter(g => g.id !== id)
            }
        }));
    };

    const handleAddGroup = (filter) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(p => p.name).join(' - '),
            excluded: filter.excluded
        };

        const existingIndex = groupFilters.findIndex(g => g.id === newFilter.id);
        const updated = [...groupFilters];

        if (existingIndex >= 0) {
            updated[existingIndex] = newFilter;
        } else {
            updated.push(newFilter);
        }

        setWizard(prev => ({
            ...prev,
            recipients: {
                ...prev.recipients,
                groups: updated
            }
        }));
    };

    const isEmployeeAudience = form.audience === FORM_AUDIENCE_TYPE.employees;
    const isEnquiryAudience = form.audience === FORM_AUDIENCE_TYPE.enquiries;
    const isExternalAudience = form.audience === FORM_AUDIENCE_TYPE.externalUsers;
    const isStudentAudience = form.audience === FORM_AUDIENCE_TYPE.students;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    const isParentApplicantStatusAudience =
        isParentAudience && ['without', 'applicant-status'].includes(details.parentAccountType);
    const filteredGroupModules = useMemo(() => {
        if (!groupModules?.modules) return [];

        const modules = groupModules.modules;

        const isEmployeeMode =
            assignmentType === FORM_ASSIGNMENT_TYPE.employees ||
            form.audience === FORM_AUDIENCE_TYPE.employees;

        const isStudentMode =
            assignmentType === FORM_ASSIGNMENT_TYPE.students ||
            form.audience === FORM_AUDIENCE_TYPE.students ||
            form.audience === FORM_AUDIENCE_TYPE.enquiries ||
            form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
            form.audience === FORM_AUDIENCE_TYPE.previousSchool;

        const isEmployeeName = (name) =>
            !!name && name.toLowerCase().includes('employee');

        if (isEmployeeMode) {
            return modules
                .filter(m =>
                    isEmployeeName(m.name) ||
                    m.filters?.some(f => f.membersType === GROUP_MEMBERS_TYPE.employee)
                )
                .map(m => ({
                    ...m,
                    filters: m.filters?.filter(f => f.membersType === GROUP_MEMBERS_TYPE.employee) || []
                }));
        }

        if (isStudentMode) {
            return modules
                .filter(m => !isEmployeeName(m.name))
                .map(m => ({
                    ...m,
                    filters: m.filters?.filter(f => f.membersType !== GROUP_MEMBERS_TYPE.employee) || []
                }))
                .filter((module) => {
                    const moduleName = (module.name || '').toUpperCase();

                    if (isEnquiryAudience) {
                        return moduleName.includes('ENQUIRY');
                    }

                    if (isStudentAudience) {
                        return !['PRE-ADMISSION', 'FUTURE-ADMISSION', 'ENQUIRY'].includes(moduleName);
                    }

                    if (isParentApplicantStatusAudience) {
                        return !['ON-ROLL', 'FUTURE-ADMISSION', 'ENQUIRY'].includes(moduleName);
                    }

                    return !moduleName.includes('ENQUIRY');
                });
        }

        return [];
    }, [
        assignmentType,
        form.audience,
        groupModules?.modules,
        isEnquiryAudience,
        isParentApplicantStatusAudience,
        isStudentAudience
    ]);

    const addGroupHeading = useMemo(() => {
        const isEmployeeMode =
            assignmentType === FORM_ASSIGNMENT_TYPE.employees ||
            form.audience === FORM_AUDIENCE_TYPE.employees;

        return isEmployeeMode
            ? 'Would you like to add employees using filters and groups? (optional)'
            : isEnquiryAudience
                ? 'Would you like to add enquiries using filters and groups? (optional)'
                : isStudentAudience
                    ? 'Would you like to add students using filters and groups? (optional)'
                    : 'Would you like to add students or applicants using filters and groups? (optional)';
    }, [assignmentType, form.audience, isEnquiryAudience, isStudentAudience]);

    const renderStudentRecipientSelectors = () => (
        <>
            {isEnquiryAudience && (
                <Box mb={2}>
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Enquiries',
                            placeholder: 'Search enquiries'
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{queryParams: {pupilType: 'enquiry'}}}
                        getOptionLabel={(o) => o.label}
                        value={recipients.student.enquiries.filter(x => !x.isDeleted)}
                        onChange={(e, newValue) =>
                            setWizard(prev => ({
                                ...prev,
                                recipients: {
                                    ...prev.recipients,
                                    student: {
                                        ...prev.recipients.student,
                                        enquiries: syncArray(newValue, prev.recipients.student.enquiries)
                                    }
                                }
                            }))
                        }
                    />
                </Box>
            )}

            {!isEnquiryAudience && !isStudentAudience && (
                <Box mb={2}>
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Applicants',
                            placeholder: 'Search applicants'
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{queryParams: {pupilType: 'applicant'}}}
                        getOptionLabel={(o) => o.label}
                        value={recipients.student.applications.filter(x => !x.isDeleted)}
                        onChange={(e, newValue) =>
                            setWizard(prev => ({
                                ...prev,
                                recipients: {
                                    ...prev.recipients,
                                    student: {
                                        ...prev.recipients.student,
                                        applications: syncArray(newValue, prev.recipients.student.applications)
                                    }
                                }
                            }))
                        }
                    />
                </Box>
            )}

            {!isEnquiryAudience && !isParentApplicantStatusAudience && (
                <Box mb={2}>
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Students',
                            placeholder: 'Search students'
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{queryParams: {pupilType: 'student'}}}
                        getOptionLabel={(o) => o.label}
                        value={recipients.student.students.filter(x => !x.isDeleted)}
                        onChange={(e, newValue) =>
                            setWizard(prev => ({
                                ...prev,
                                recipients: {
                                    ...prev.recipients,
                                    student: {
                                        ...prev.recipients.student,
                                        students: syncArray(newValue, prev.recipients.student.students)
                                    }
                                }
                            }))
                        }
                    />
                </Box>
            )}
        </>
    );

    const renderEmployeeRecipientSelectors = () => (
        <PaginatedAutocomplete
            textFieldProps={{
                label: 'Employees',
                placeholder: 'Search employees'
            }}
            fetchFn={useLazyGetAllEmployeeProfilesQuery}
            getOptionLabel={(o) => o.label || `${o.preferredForename ?? ''} ${o.preferredSurname ?? ''}`}
            value={recipients.employees.filter(x => !x.isDeleted)}
            onChange={(e, newValue) =>
                setWizard(prev => ({
                    ...prev,
                    recipients: {
                        ...prev.recipients,
                        employees: syncArray(newValue, prev.recipients.employees)
                    }
                }))
            }
        />
    );

    const shouldShowRecipientSelectors = (() => {
        if (isExternalAudience) {
            return assignmentType !== FORM_ASSIGNMENT_TYPE.none;
        }

        return (
            isEmployeeAudience ||
            form.audience === FORM_AUDIENCE_TYPE.students ||
            form.audience === FORM_AUDIENCE_TYPE.enquiries ||
            form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
            form.audience === FORM_AUDIENCE_TYPE.previousSchool
        );
    })();

    const showEmployeeFields =
        isEmployeeAudience ||
        (isExternalAudience && assignmentType === FORM_ASSIGNMENT_TYPE.employees);

    const showStudentFields =
        (!isEmployeeAudience &&
            (
                form.audience === FORM_AUDIENCE_TYPE.students ||
                form.audience === FORM_AUDIENCE_TYPE.enquiries ||
                form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion ||
                form.audience === FORM_AUDIENCE_TYPE.previousSchool ||
                (isExternalAudience && assignmentType === FORM_ASSIGNMENT_TYPE.students)
            ));

    useEffect(() => {
        if (activeGroupCount > 1 || !details.strictRecipientsFiltering) return;

        setWizard(prev => ({
            ...prev,
            details: {
                ...prev.details,
                strictRecipientsFiltering: false,
            },
        }));
    }, [activeGroupCount, details.strictRecipientsFiltering, setWizard]);

    const hasRecipientAssignments =
        [
            ...(recipients.groups || []),
            ...(recipients.employees || []),
            ...(recipients.student?.students || []),
            ...(recipients.student?.applications || []),
            ...(recipients.student?.enquiries || []),
        ].some(x => x && !x.isDeleted && !x.deleted);

    return (
        <>
            <Box sx={{p: 3}}>
                {isExternalAudience && (
                    <Box sx={{mb: 1}}>
                        <Typography fontWeight="600" my={1}>
                            Would you like to associate the external recipients with students/applicants or employees?
                        </Typography>

                        <FormControl sx={{
                            my: 3,
                            minWidth: 340
                        }}>
                            <InputLabel>Recipient Type</InputLabel>

                            <Select
                                disabled={hasRecipientAssignments}
                                label="Recipient Type"
                                value={assignmentType}
                                onChange={(e) =>
                                    setWizard(prev => ({
                                        ...clearRecipientFields(prev),
                                        recipients: {
                                            ...prev.recipients,
                                            assignmentType: e.target.value,
                                        }
                                    }))
                                }
                            >
                                <MenuItem value={FORM_ASSIGNMENT_TYPE.none}>None</MenuItem>
                                <MenuItem value={FORM_ASSIGNMENT_TYPE.students}>Students/Applicants</MenuItem>
                                <MenuItem value={FORM_ASSIGNMENT_TYPE.employees}>Employees</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>
                )}

                {shouldShowRecipientSelectors && (
                    <>
                        <Box sx={{mt: 0}}>
                            <Typography variant="subtitle1" fontWeight="bold">
                                {addGroupHeading}
                            </Typography>

                            <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1, mb: 2}}>
                                <Button
                                    variant="outlined"
                                    startIcon={<AddOutlinedIcon/>}
                                    onClick={() => setOpenAddGroupDialog(true)}
                                    sx={{
                                        minWidth: 160,
                                    }}
                                >
                                    Add Group
                                </Button>
                                <Button
                                    variant="contained"
                                    disabled={groupFilters.length === 0}
                                    onClick={() => setPreviewDialogOpen(true)}
                                    sx={{
                                        minWidth: 120,
                                        boxShadow: 'none',
                                        '&:hover': {
                                            boxShadow: 'none',
                                        },
                                    }}
                                >
                                    Preview
                                </Button>
                            </Box>
                            <AdmicityTableV2
                                emptyStateMinHeight={80}
                                noRowsOverlay={{ text: 'No groups' }}
                                columns={[
                                    {
                                        accessorKey: 'displayName',
                                        header: 'Group Name',
                                        grow: true
                                    },
                                    {
                                        accessorKey: 'excluded',
                                        header: 'Status',
                                        size: 100,
                                        Cell: ({cell}) =>
                                            cell.getValue()
                                                ? <Typography color="error.main">Excluded</Typography>
                                                : <Typography color="success.main">Included</Typography>
                                    }
                                ]}
                                data={groupFilters}
                                tableProps={{
                                    layoutMode: 'grid',
                                    enableRowActions: true,
                                    enableSorting: false,
                                    enableFilters: false,
                                    enableHiding: false,
                                    enableFullScreenToggle: false,
                                    enableColumnResizing: false,
                                    enableBottomToolbar: false,
                                    enableTopToolbar: false,
                                    enablePagination: false,
                                    enableStickyHeader: true,
                                    enableStickyFooter: false,
                                    muiTablePaperProps: {
                                        variant: 'outlined',
                                        elevation: 0,
                                        sx: {
                                            display: 'block',
                                            flexDirection: 'unset',
                                            flexGrow: 0,
                                            width: '100%',
                                            height: 'auto',
                                        }
                                    },
                                    muiTableContainerProps: {
                                        sx: {
                                            flex: 'unset',
                                            maxHeight: 350,
                                            minHeight: 'auto',
                                            overflow: 'auto',
                                        }
                                    },
                                    muiTableBodyProps: {
                                        sx: {
                                            '& :last-child td': {
                                                borderBottom: 0,
                                            },
                                        }
                                    },
                                    renderRowActions: ({row}) => (
                                        <IconButton
                                            onClick={() => handleRemoveGroup(row.original.id)}
                                            size="small"
                                        >
                                            <DeleteOutlineRoundedIcon fontSize="small"/>
                                        </IconButton>
                                    )
                                }}
                            />
                            {groupFilters.length > 1 && (
                                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={details.strictRecipientsFiltering || false}
                                                onChange={(e) =>
                                                    setWizard(prev => ({
                                                        ...prev,
                                                        details: {
                                                            ...prev.details,
                                                            strictRecipientsFiltering: e.target.checked,
                                                        },
                                                    }))
                                                }
                                            />
                                        }
                                        label={
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5
                                            }}>
                                                Must appear in all selected groups
                                                <Tooltip
                                                    title="Only recipients who belong to every included group will receive this form. Useful for narrowing groups with overlaps."
                                                    placement="right"
                                                >
                                                    <InfoOutlinedIcon fontSize="small"
                                                                      sx={{color: 'text.secondary'}}/>
                                                </Tooltip>
                                            </Box>
                                        }
                                    />
                                </Box>
                            )}
                            {groupFilters.length > 0 && (
                                <Box
                                    sx={{
                                        mt: 1,
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}
                                >
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 0.5}}>
                                        <Typography variant="body2">
                                            Recipients from all included groups will receive the form.
                                        </Typography>
                                        <Tooltip
                                            title="To exclude a group, open the Add Group dialog and mark it excluded.">
                                            <InfoOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                        </Tooltip>
                                    </Box>
                                </Box>
                            )}
                        </Box>

                        <Typography variant="subtitle1" sx={{
                            fontWeight: 600,
                            mt: 4,
                            mb: 2
                        }}>
                                {showEmployeeFields
                                ? 'Would you like to add employees individually? (optional)'
                                : isEnquiryAudience
                                    ? 'Would you like to add enquiries individually? (optional)'
                                    : isStudentAudience
                                        ? 'Would you like to add students individually? (optional)'
                                        : isParentApplicantStatusAudience
                                            ? 'Would you like to add applicants individually? (optional)'
                                            : 'Would you like to add students or applicants individually? (optional)'}
                        </Typography>

                        {showEmployeeFields && renderEmployeeRecipientSelectors()}
                        {showEmployeeFields && (details.linkToStudents || details.linkToEmployees) && (
                            <Box sx={{pt: 2, mb: 2}}>
                                <SingleChoice
                                    id="allLinkedEmployeesCanSubmit"
                                    fieldKey="allLinkedEmployeesCanSubmit"
                                    descriptionHtml={`<strong>${LINKED_EMPLOYEE_SUBMISSION_QUESTION}</strong>`}
                                    value={details.allLinkedEmployeesCanSubmit ? 'true' : 'false'}
                                    options={LINKED_EMPLOYEE_SUBMISSION_OPTIONS}
                                    isEditMode={false}
                                    onChange={({radioValue}) =>
                                        setWizard(prev => ({
                                            ...prev,
                                            details: {
                                                ...prev.details,
                                                allLinkedEmployeesCanSubmit: radioValue === 'true'
                                            }
                                        }))
                                    }
                                />
                            </Box>
                        )}
                        {showStudentFields && renderStudentRecipientSelectors()}
                    </>
                )}

                <AddStudentGroupDialog
                    open={openAddGroupDialog}
                    groupFilters={filteredGroupModules}
                    onClose={() => setOpenAddGroupDialog(false)}
                    onChange={handleAddGroup}
                />
                <RecipientsPreviewDialog
                    open={previewDialogOpen}
                    onClose={() => setPreviewDialogOpen(false)}
                    formId={details.formId ?? null}
                    groupFilters={groupFilters}
                    strictRecipientsFiltering={details.strictRecipientsFiltering}
                    audience={form.audience}
                    accessLevel={wizardAccessLevel ?? details.accessLevel}
                    recipientType={recipients.assignmentType}
                    externalEmails={recipients.externalEmails}
                    sendToPriority1ContactsOnly={details.sendToPriority1ContactsOnly}
                />
            </Box>
        </>
    );
};

export const StepEmployeeStudentAssociation = ({
                                                   wizard,
                                                   setWizard,
                                                   wizardAccessLevel = null
                                               }) => {
    const {
        form,
        associations,
        details
    } = wizard;

    if (form.type === 'triggerForm') return null;

    const isEmployeeAudience = form.audience === FORM_AUDIENCE_TYPE.employees;
    if (!isEmployeeAudience) return null;

    if (!details.linkToStudents) return null;

    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);

    const {data: groupModules} = useGetGroupFiltersQuery({}, {skip: false});
    const {data: schoolInfo} = useGetCurrentSchoolQuery({}, {skip: false});

    const groupFilters = associations?.groups || [];

    const syncArray = (newValue, oldValue = []) => {
        if (!Array.isArray(newValue)) return [];

        const map = new Map();

        newValue.forEach(item => {
            if (item?.id != null) map.set(item.id, item);
        });

        oldValue.forEach(item => {
            if (map.has(item.id) && item.assignmentId) {
                map.set(item.id, {
                    ...map.get(item.id),
                    assignmentId: item.assignmentId
                });
            }
        });

        return [...map.values()];
    };

    const handleRemoveGroup = (id) => {
        setWizard(prev => ({
            ...prev,
            associations: {
                ...prev.associations,
                groups: (prev.associations.groups || []).filter(g => g.id !== id)
            }
        }));
    };

    const handleAddGroup = (filter) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(p => p.name).join(' - '),
            excluded: filter.excluded
        };

        const existingIndex = groupFilters.findIndex(g => g.id === newFilter.id);
        const updated = [...groupFilters];

        if (existingIndex >= 0) {
            updated[existingIndex] = newFilter;
        } else {
            updated.push(newFilter);
        }

        setWizard(prev => ({
            ...prev,
            associations: {
                ...prev.associations,
                groups: updated
            }
        }));
    };

    const isEmployeeName = (name) =>
        !!name && name.toLowerCase().includes('employee');

    const filteredGroupModules = useMemo(() => {
        if (!groupModules?.modules) return [];

        return groupModules.modules
            .filter(m => !isEmployeeName(m.name))
            .map(m => ({
                ...m,
                filters: (m.filters || []).filter(f => f.membersType !== GROUP_MEMBERS_TYPE.employee)
            }))
            .filter(m => (m.filters || []).length > 0);
    }, [groupModules?.modules]);

    const renderStudentAssociationSelectors = () => (
        <>
            {schoolInfo && !schoolInfo.disableEnquiriesSync && (
                <Box mb={2}>
                    <PaginatedAutocomplete
                        textFieldProps={{
                            label: 'Enquiries',
                            placeholder: 'Search enquiries'
                        }}
                        fetchFn={useLazyGetStudentFiltersQuery}
                        fetchFnProps={{queryParams: {pupilType: 'enquiry'}}}
                        getOptionLabel={(o) => o.label}
                        value={(associations.enquiries || []).filter(x => !x.isDeleted)}
                        onChange={(e, newValue) =>
                            setWizard(prev => ({
                                ...prev,
                                associations: {
                                    ...prev.associations,
                                    enquiries: syncArray(newValue, prev.associations.enquiries)
                                }
                            }))
                        }
                    />
                </Box>
            )}

            <Box mb={2}>
                <PaginatedAutocomplete
                    textFieldProps={{
                        label: 'Applicants',
                        placeholder: 'Search applicants'
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{queryParams: {pupilType: 'applicant'}}}
                    getOptionLabel={(o) => o.label}
                    value={(associations.applications || []).filter(x => !x.isDeleted)}
                    onChange={(e, newValue) =>
                        setWizard(prev => ({
                            ...prev,
                            associations: {
                                ...prev.associations,
                                applications: syncArray(newValue, prev.associations.applications)
                            }
                        }))
                    }
                />
            </Box>

            <Box mb={2}>
                <PaginatedAutocomplete
                    textFieldProps={{
                        label: 'Students',
                        placeholder: 'Search students'
                    }}
                    fetchFn={useLazyGetStudentFiltersQuery}
                    fetchFnProps={{queryParams: {pupilType: 'student'}}}
                    getOptionLabel={(o) => o.label}
                    value={(associations.students || []).filter(x => !x.isDeleted)}
                    onChange={(e, newValue) =>
                        setWizard(prev => ({
                            ...prev,
                            associations: {
                                ...prev.associations,
                                students: syncArray(newValue, prev.associations.students)
                            }
                        }))
                    }
                />
            </Box>
        </>
    );

    useEffect(() => {
        if (groupFilters.length > 1 || !details.linkedStrictFiltering) return;

        setWizard(prev => ({
            ...prev,
            details: {
                ...prev.details,
                linkedStrictFiltering: false,
            },
        }));
    }, [details.linkedStrictFiltering, groupFilters.length, setWizard]);

    return (
        <Box sx={{p: 3}}>
            <Typography variant="subtitle1" fontWeight="600" sx={{mb: 2}}>
                Select which students this employee form should be linked to (optional):
            </Typography>

            <Box sx={{mt: 0}}>

                <Typography variant="subtitle1" fontWeight="bold">
                    Would you like to add students using filters and groups? (optional)
                </Typography>

                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1, mb: 2}}>
                    <Button
                        variant="outlined"
                        startIcon={<AddOutlinedIcon/>}
                        onClick={() => setOpenAddGroupDialog(true)}
                        sx={{
                            minWidth: 160,
                        }}
                    >
                        Add Group
                    </Button>
                    <Button
                        variant="contained"
                        disabled={groupFilters.length === 0}
                        onClick={() => setPreviewDialogOpen(true)}
                        sx={{
                            minWidth: 120,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Preview
                    </Button>
                </Box>
                <>
                    <AdmicityTableV2
                        emptyStateMinHeight={80}
                        noRowsOverlay={{text: 'No groups'}}
                        columns={[
                            {
                                accessorKey: 'displayName',
                                header: 'Group Name',
                                grow: true
                            },
                            {
                                accessorKey: 'excluded',
                                header: 'Status',
                                size: 100,
                                Cell: ({cell}) =>
                                    cell.getValue()
                                        ? <Typography color="error.main">Excluded</Typography>
                                        : <Typography color="success.main">Included</Typography>
                            }
                        ]}
                        data={groupFilters}
                        tableProps={{
                            layoutMode: 'grid',
                            enableRowActions: true,
                            enableSorting: false,
                            enableFilters: false,
                            enableHiding: false,
                            enableFullScreenToggle: false,
                            enableColumnResizing: false,
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            enablePagination: false,
                            enableStickyHeader: true,
                            enableStickyFooter: false,
                            muiTablePaperProps: {
                                variant: 'outlined',
                                elevation: 0,
                                sx: {
                                    display: 'block',
                                    flexDirection: 'unset',
                                    flexGrow: 0,
                                    width: '100%',
                                    height: 'auto',
                                }
                            },
                            muiTableContainerProps: {
                                sx: {
                                    flex: 'unset',
                                    maxHeight: 350,
                                    minHeight: 'auto',
                                    overflow: 'auto',
                                }
                            },
                            muiTableBodyProps: {
                                sx: {
                                    '& :last-child td': {
                                        borderBottom: 0,
                                    },
                                },
                            },
                            renderRowActions: ({row}) => (
                                <IconButton
                                    onClick={() => handleRemoveGroup(row.original.id)}
                                    size="small"
                                >
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )
                        }}
                    />

                    {groupFilters.length > 1 && (
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={details.linkedStrictFiltering || false}
                                        onChange={(e) =>
                                            setWizard(prev => ({
                                                ...prev,
                                                details: {
                                                    ...prev.details,
                                                    linkedStrictFiltering: e.target.checked,
                                                },
                                            }))
                                        }
                                    />
                                }
                                label={
                                    <Box sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.5
                                    }}>
                                        Must appear in all selected groups
                                        <Tooltip
                                            title="Only students who belong to every included group will be linked to the form. Useful for narrowing groups with overlaps."
                                            placement="right"
                                        >
                                            <InfoOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                                        </Tooltip>
                                    </Box>
                                }
                            />
                        </Box>
                    )}

                    <Box
                        sx={{
                            mt: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                        }}
                    >
                        <Typography variant="body2">
                            Students from all included groups will be linked to the form.
                        </Typography>
                        <Tooltip title="To exclude a group, open the Add Group dialog and mark it excluded.">
                            <InfoOutlinedIcon fontSize="small" sx={{color: 'text.secondary'}}/>
                        </Tooltip>
                    </Box>
                </>

            </Box>

            <Typography
                variant="subtitle1"
                sx={{
                    fontWeight: 600,
                    mt: 4,
                    mb: 3
                }}
            >
                Would you like to add individual students or applicants?
            </Typography>

            {renderStudentAssociationSelectors()}

            <AddStudentGroupDialog
                open={openAddGroupDialog}
                groupFilters={filteredGroupModules}
                onClose={() => setOpenAddGroupDialog(false)}
                onChange={handleAddGroup}
            />
            <RecipientsPreviewDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                formId={details.formId ?? null}
                groupFilters={groupFilters}
                strictRecipientsFiltering={details.linkedStrictFiltering}
                audience={FORM_AUDIENCE_TYPE.students}
                accessLevel={wizardAccessLevel ?? details.accessLevel}
            />
        </Box>
    );
};

export const StepEmployeeEmployeeAssociation = ({
                                                    wizard,
                                                    setWizard,
                                                    wizardAccessLevel = null
                                                }) => {
    const {
        form,
        associations,
        details
    } = wizard;

    if (form.type === 'triggerForm') return null;

    const isEmployeeAudience = form.audience === FORM_AUDIENCE_TYPE.employees;
    if (!isEmployeeAudience) return null;
    if (!details.linkToEmployees) return null;

    const [openAddGroupDialog, setOpenAddGroupDialog] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const {data: groupModules} = useGetGroupFiltersQuery({}, {skip: false});
    const groupFilters = associations?.groups || [];

    const syncArray = (newValue, oldValue = []) => {
        if (!Array.isArray(newValue)) return [];
        const map = new Map();
        newValue.forEach(item => {
            if (item?.id != null) map.set(item.id, item);
        });
        oldValue.forEach(item => {
            if (map.has(item.id) && item.assignmentId) {
                map.set(item.id, {
                    ...map.get(item.id),
                    assignmentId: item.assignmentId
                });
            }
        });
        return [...map.values()];
    };

    const handleRemoveGroup = (id) => {
        setWizard(prev => ({
            ...prev,
            associations: {
                ...prev.associations,
                groups: (prev.associations.groups || []).filter(g => g.id !== id)
            }
        }));
    };

    const handleAddGroup = (filter) => {
        const newFilter = {
            id: filter.filterId,
            displayName: filter.paths.map(p => p.name).join(' - '),
            excluded: filter.excluded
        };
        const existingIndex = groupFilters.findIndex(g => g.id === newFilter.id);
        const updated = [...groupFilters];
        if (existingIndex >= 0) {
            updated[existingIndex] = newFilter;
        } else {
            updated.push(newFilter);
        }
        setWizard(prev => ({
            ...prev,
            associations: {
                ...prev.associations,
                groups: updated
            }
        }));
    };

    const filteredGroupModules = useMemo(() => {
        if (!groupModules?.modules) return [];
        return groupModules.modules
            .filter(m =>
                m.name === 'EMPLOYEES' ||
                m.name?.toLowerCase().includes('school group') ||
                m.name?.toLowerCase().includes('my group'))
            .map(m => ({
                ...m,
                filters: (m.filters || []).filter(f => f.membersType === GROUP_MEMBERS_TYPE.employee)
            }));
    }, [groupModules?.modules]);

    useEffect(() => {
        if (groupFilters.length > 1 || !details.linkedStrictFiltering) return;
        setWizard(prev => ({
            ...prev,
            details: {
                ...prev.details,
                linkedStrictFiltering: false,
            },
        }));
    }, [details.linkedStrictFiltering, groupFilters.length, setWizard]);

    return (
        <Box sx={{p: 3}}>
            <Typography variant="subtitle1" fontWeight="600" sx={{mb: 2}}>
                Select which employees this employee form should be linked to (optional):
            </Typography>

            <Box sx={{mt: 0}}>
                <Typography variant="subtitle1" fontWeight="bold">
                    Would you like to add employees using filters and groups? (optional)
                </Typography>
                <Box sx={{display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mt: 1, mb: 2}}>
                    <Button
                        variant="outlined"
                        startIcon={<AddOutlinedIcon/>}
                        onClick={() => setOpenAddGroupDialog(true)}
                        sx={{
                            minWidth: 160,
                        }}
                    >
                        Add Group
                    </Button>
                    <Button
                        variant="contained"
                        disabled={groupFilters.length === 0}
                        onClick={() => setPreviewDialogOpen(true)}
                        sx={{
                            minWidth: 120,
                            boxShadow: 'none',
                            '&:hover': {
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Preview
                    </Button>
                </Box>
                <>
                    <AdmicityTableV2
                        emptyStateMinHeight={80}
                        noRowsOverlay={{text: 'No groups'}}
                        columns={[
                            {
                                accessorKey: 'displayName',
                                header: 'Group Name',
                                grow: true
                            },
                            {
                                accessorKey: 'excluded',
                                header: 'Status',
                                size: 100,
                                Cell: ({cell}) =>
                                    cell.getValue()
                                        ? <Typography color="error.main">Excluded</Typography>
                                        : <Typography color="success.main">Included</Typography>
                            }
                        ]}
                        data={groupFilters}
                        tableProps={{
                            layoutMode: 'grid',
                            enableRowActions: true,
                            enableSorting: false,
                            enableFilters: false,
                            enableHiding: false,
                            enableFullScreenToggle: false,
                            enableColumnResizing: false,
                            enableBottomToolbar: false,
                            enableTopToolbar: false,
                            enablePagination: false,
                            enableStickyHeader: true,
                            enableStickyFooter: false,
                            muiTablePaperProps: {
                                variant: 'outlined',
                                elevation: 0,
                                sx: {
                                    display: 'block',
                                    flexDirection: 'unset',
                                    flexGrow: 0,
                                    width: '100%',
                                    height: 'auto',
                                }
                            },
                            muiTableContainerProps: {
                                sx: {
                                    flex: 'unset',
                                    maxHeight: 350,
                                    minHeight: 'auto',
                                    overflow: 'auto',
                                }
                            },
                            muiTableBodyProps: {
                                sx: {
                                    '& :last-child td': {
                                        borderBottom: 0,
                                    },
                                },
                            },
                            renderRowActions: ({row}) => (
                                <IconButton
                                    onClick={() => handleRemoveGroup(row.original.id)}
                                    size="small"
                                >
                                    <DeleteOutlineRoundedIcon fontSize="small"/>
                                </IconButton>
                            )
                        }}
                    />
                    {groupFilters.length > 1 && (
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1}}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={details.linkedStrictFiltering || false}
                                        onChange={(e) =>
                                            setWizard(prev => ({
                                                ...prev,
                                                details: {
                                                    ...prev.details,
                                                    linkedStrictFiltering: e.target.checked
                                                }
                                            }))
                                        }
                                    />
                                }
                                label="Only include employees who match all selected groups"
                            />
                        </Box>
                    )}
                </>
            </Box>

            <Typography variant="subtitle1" fontWeight="600" sx={{mt: 4, mb: 3}}>
                Would you like to add individual employees?
            </Typography>
            <PaginatedAutocomplete
                textFieldProps={{
                    label: 'Employees',
                    placeholder: 'Enter name, surname, or full name'
                }}
                fetchFn={useLazyGetAllEmployeeProfilesQuery}
                getOptionLabel={(o) => o.label || `${o.preferredForename ?? ''} ${o.preferredSurname ?? ''}`.trim()}
                value={(associations.employees || []).filter(x => !x.isDeleted)}
                onChange={(e, newValue) =>
                    setWizard(prev => ({
                        ...prev,
                        associations: {
                            ...prev.associations,
                            employees: syncArray(newValue, prev.associations.employees)
                        }
                    }))
                }
            />
            <AddStudentGroupDialog
                open={openAddGroupDialog}
                groupFilters={filteredGroupModules}
                onClose={() => setOpenAddGroupDialog(false)}
                onChange={handleAddGroup}
            />
            <RecipientsPreviewDialog
                open={previewDialogOpen}
                onClose={() => setPreviewDialogOpen(false)}
                formId={details.formId ?? null}
                groupFilters={groupFilters}
                strictRecipientsFiltering={details.linkedStrictFiltering}
                audience={FORM_AUDIENCE_TYPE.employees}
                accessLevel={wizardAccessLevel ?? details.accessLevel}
            />
        </Box>
    );
};

export const StepRecipientBothParents = ({
                                             wizard,
                                             setWizard
                                         }) => {
    const {
        form,
        details
    } = wizard;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    if (!isParentAudience) return null;
    return (
        <>
            <Box sx={{
                mb: 3,
                p: 3
            }}>
                <SingleChoice
                    id="allParentsCanSubmit"
                    fieldKey="allParentsCanSubmit"
                    descriptionHtml="<strong>Do you require both parents to complete this form?</strong>"
                    value={details.allParentsCanSubmit ? 'true' : 'false'}
                    options={[{
                        id: 'false',
                        description: 'No'
                    }, {
                        id: 'true',
                        description: 'Yes'
                    }]}
                    isEditMode={false}
                    onChange={({radioValue}) =>
                        setWizard(prev => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                allParentsCanSubmit: radioValue === 'true'
                            }
                        }))
                    }
                />
            </Box>
        </>
    )
}

export const StepSendToPriority1Only = ({
                                           wizard,
                                           setWizard
                                       }) => {
    const {
        form,
        details
    } = wizard;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    if (!isParentAudience) return null;
    return (
        <>
            <Box sx={{
                mb: 3,
                p: 3
            }}>
                <SingleChoice
                    id="sendToPriority1ContactsOnly"
                    fieldKey="sendToPriority1ContactsOnly"
                    descriptionHtml={details.isCommunication
                        ? '<strong>Which contacts of the student or applicant should this email be sent to?</strong>'
                        : '<strong>Which contacts of the student or applicant should this form be sent to?</strong>'}
                    value={details.sendToPriority1ContactsOnly ? 'true' : 'false'}
                    options={[{
                        id: 'false',
                        description: 'Priority 1 and 2 contacts'
                    }, {
                        id: 'true',
                        description: 'Priority 1 contacts only'
                    }]}
                    isEditMode={false}
                    onChange={({radioValue}) =>
                        setWizard(prev => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                sendToPriority1ContactsOnly: radioValue === 'true'
                            }
                        }))
                    }
                />
            </Box>
        </>
    )
}

export const StepVisibleToParents = ({
                                         wizard,
                                         setWizard,
                                         wizardAccessLevel
                                     }) => {
    const {
        form,
        details
    } = wizard;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    const isPublicWithStudentLink = wizardAccessLevel === FORM_ACCESS_LEVELS.publicWithStudentLink;

    if (!isParentAudience || !isPublicWithStudentLink) return null;

    return (
        <>
            <Box sx={{
                mb: 3,
                p: 3
            }}>
                <SingleChoice
                    id="visibleToParents"
                    fieldKey="visibleToParents"
                    descriptionHtml="<strong>Show this form to parents in their account and on the student profile?</strong>"
                    value={(details.visibleToParents ?? true) ? 'true' : 'false'}
                    options={[{
                        id: 'false',
                        description: 'No'
                    }, {
                        id: 'true',
                        description: 'Yes'
                    }]}
                    isEditMode={false}
                    onChange={({radioValue}) =>
                        setWizard(prev => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                visibleToParents: radioValue === 'true'
                            }
                        }))
                    }
                />
            </Box>
        </>
    )
}

export const StepShareResponseWithOtherParents = ({
                                                      wizard,
                                                      setWizard,
                                                      wizardAccessLevel
                                                  }) => {
    const {
        form,
        details
    } = wizard;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    const isPrivate = wizardAccessLevel === FORM_ACCESS_LEVELS.private;
    const isSharedRequest = !details.allParentsCanSubmit;

    if (!isParentAudience || !isPrivate || !isSharedRequest) return null;

    return (
        <>
            <Box sx={{
                mb: 3,
                p: 3
            }}>
                <SingleChoice
                    id="shareResponseWithOtherParents"
                    fieldKey="shareResponseWithOtherParents"
                    descriptionHtml="<strong>Should the submitted response be visible to other assigned parents?</strong>"
                    value={(details.shareResponseWithOtherParents ?? true) ? 'true' : 'false'}
                    options={[{
                        id: 'false',
                        description: 'No'
                    }, {
                        id: 'true',
                        description: 'Yes'
                    }]}
                    isEditMode={false}
                    onChange={({radioValue}) =>
                        setWizard(prev => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                shareResponseWithOtherParents: radioValue === 'true'
                            }
                        }))
                    }
                />
            </Box>
        </>
    )
}

export const StepCompletedProfiles = ({
                                          wizard,
                                          setWizard
                                      }) => {
    const {
        form,
        details
    } = wizard;
    const isParentAudience = form.audience === FORM_AUDIENCE_TYPE.parentsForChildCompletion;
    if (!isParentAudience) return null;
    return (
        <>
            <Box sx={{
                mb: 3,
                p: 3
            }}>
                <SingleChoice
                    id="completedprofiles"
                    fieldKey="completedprofiles"
                    descriptionHtml="<strong>Would you like to block form submission until the student's profile is complete?</strong>"
                    value={details.profileMustBeComplete ? 'true' : 'false'}
                    options={[{
                        id: 'false',
                        description: 'No'
                    }, {
                        id: 'true',
                        description: 'Yes'
                    }]}
                    isEditMode={false}
                    onChange={({radioValue}) =>
                        setWizard(prev => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                profileMustBeComplete: radioValue === 'true'
                            }
                        }))
                    }
                />
            </Box>
        </>
    )
}

export const StepCreationMethod = ({
                                       wizard,
                                       setWizard
                                   }) => {
    useEffect(() => {
        if (!wizard.template.creationMethod) {
            setWizard(prev => ({
                ...prev,
                template: {
                    ...prev.template,
                    creationMethod: 'template'
                }
            }));
        }
    }, [wizard.template.creationMethod, setWizard]);

    const handleChange = ({radioValue}) => {
        if (!radioValue) return;

        setWizard(prev => {
            const next = {
                ...prev,
                template: {
                    ...prev.template,
                    creationMethod: radioValue
                }
            };

            if (['blank', 'template', 'schoolTemplate'].includes(radioValue)) {
                next.template = {
                    ...next.template,
                    creationMethod: radioValue,
                    templateId: null,
                    templateObject: null,
                };
            }

            return next;
        });
    };

    const options = [
        {
            id: 'template',
            description: 'Use an Admicity template',
        },
        {
            id: 'schoolTemplate',
            description: 'Use a school template',
        },
    ];

    return (
        <Box sx={{p: 3}}>
            <SingleChoice
                componentId="creationMethod"
                fieldKey="creationMethod"
                descriptionHtml="<strong>How would you like to create this form?</strong>"
                value={
                    wizard.template.creationMethod ||
                    'template'
                }
                options={options}
                isEditMode={false}
                onChange={handleChange}
            />
        </Box>
    );
};

export const StepTemplateSelection = ({
                                          wizard,
                                          setWizard,
                                          onSelected,
                                          onBack,
                                          templateSource
                                      }) => (
    <Box sx={{p: 3}}>
        <CreateFormTemplatePreviewList
            templateSource={templateSource}
            onBack={onBack}
            audience={wizard.form.audience}
            linkToStudents={wizard.details?.linkToStudents}
            parentAccountType={wizard.details.parentAccountType}
            formType={wizard.form.type}
            recipientAssignmentType={wizard.recipients.assignmentType}
            triggerRecipientAssignmentType={
                wizard.form.type === FORM_TYPE.triggerForm
                    ? wizard.trigger?.selectedForm?.assignmentType
                    : null
            }
            onTemplateSelected={(template) => {
                setWizard(prev => {
                    const previousTemplateName =
                        prev.template.templateObject?.name ||
                        prev.template.templateObject?.formName ||
                        '';
                    const nextTemplateName = template.name || template.formName || '';
                    const shouldUpdateFormName =
                        template.category !== 'Blank' &&
                        (
                            !prev.details.formName ||
                            prev.details.formName === previousTemplateName
                        );

                    let newAudience = prev.form.audience;
                    if (
                        template.type === 'offer' &&
                        prev.form.audience !== FORM_AUDIENCE_TYPE.enquiries
                    ) {
                        newAudience = FORM_AUDIENCE_TYPE.parentsForChildCompletion;
                    } else if (template.type === 'transition') {
                        newAudience = FORM_AUDIENCE_TYPE.previousSchool;
                    }

                    let newAccess;
                    if (['transition', 'offer'].includes(template.type)) {
                        newAccess = FORM_ACCESS_LEVELS.publicWithStudentLink;
                    } else if (['acceptance', 'application', 'shareable'].includes(template.type)) {
                        newAccess = FORM_ACCESS_LEVELS.public;
                    } else {
                        newAccess = FORM_ACCESS_LEVELS.private;
                    }

                    return {
                        ...prev,

                        template: {
                            ...prev.template,
                            templateId: template.id,
                            templateObject: template,
                        },

                        form: {
                            ...prev.form,
                            audience: newAudience
                        },

                        details: {
                            ...prev.details,
                            accessLevel: newAccess,
                            formName: shouldUpdateFormName
                                ? nextTemplateName
                                : prev.details.formName
                        }
                    };
                });

                onSelected?.();
            }}
        />
    </Box>
);

const FORM_NAME_MAX_LENGTH = 128;

export const StepFormDetails1 = ({
                                     wizard,
                                     setWizard,
                                     categories
                                 }) => {
    const selected = wizard.details.categories || [];
    const formNameValidation = validateFieldLength(wizard.details.formName, FORM_NAME_MAX_LENGTH);
    const heading = wizard.details.isCommunication ? 'Details' : 'Form Details';
    const questionOne = wizard.details.isCommunication ? 'What would you like to name this email?' : 'What would you like to name this form?';
    const questionTwo = wizard.details.isCommunication ? '  Would you like to add one or more categories to this email? (optional)' : '  Would you like to add one or more categories to this form? (optional)';
    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                p: 3
            }}
        >
            <Typography variant="h6" sx={{fontWeight: 600}}>
                {heading}
            </Typography>

            {wizard.template.templateObject && (
                <Typography
                    variant="body2"
                    sx={{color: 'text.secondary'}}
                >
                    Template selected:{' '}
                    <strong>{wizard.template.templateObject.name}</strong>
                </Typography>
            )}

            <Typography variant="body1" color="text.primary" my={1}>
                <strong>{questionOne}</strong>
                {wizard.form.type !== 'application' && wizard.form.type !== 'shareable' && wizard.form.type !== 'noEmail' && wizard.form.type !== 'selfService' &&
                    ' – this will be the subject title when sent via email'}
            </Typography>

            <TextField
                label="Form name"
                fullWidth
                autoComplete="off"
                required
                value={wizard.details.formName}
                error={!formNameValidation.isValid}
                helperText={formNameValidation.message || ' '}
                inputProps={{maxLength: FORM_NAME_MAX_LENGTH}}
                onChange={(e) =>
                    setWizard((prev) => ({
                        ...prev,
                        details: {
                            ...prev.details,
                            formName: e.target.value,
                        },
                    }))
                }
            />

            <Typography fontWeight="600" variant="body1" color="text.primary" my={1}>
                {questionTwo}
            </Typography>

            <FormControl fullWidth>
                <InputLabel id="category-label">Category (optional)</InputLabel>

                <Select
                    labelId="category-label"
                    multiple
                    label="Category (optional)"
                    value={selected}
                    onChange={(e) =>
                        setWizard((prev) => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                categories: e.target.value,
                            },
                        }))
                    }
                    renderValue={(selectedIds) => {
                        if (!categories?.length || !selectedIds?.length) {
                            return <em>No categories</em>;
                        }

                        return (
                        <Stack flexDirection="row" gap={1} flexWrap="wrap">
                            {selectedIds.map((id) => {
                                const cat = categories.find((c) => c.id === id);
                                if (!cat) return null;

                                return (
                                    <Box
                                        key={id}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 0.8,
                                            padding: 1,
                                            borderRadius: '18px',
                                            border: '1px solid #adadad',
                                        }}
                                    >
                                        <CircleIcon
                                            fontSize="small"
                                            style={{color: cat.color}}
                                        />
                                        <Typography variant="body2">
                                            {cat.name}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Stack>
                        );
                    }}
                >
                    {!categories?.length && (
                        <MenuItem disabled>
                            <em>No categories</em>
                        </MenuItem>
                    )}
                    {categories.map((cat) => (
                        <MenuItem key={cat.id} value={cat.id}>
                            <Checkbox checked={selected.includes(cat.id)}/>

                            <CircleIcon
                                fontSize="small"
                                style={{
                                    color: cat.color,
                                    marginRight: 8,
                                }}
                            />

                            <ListItemText primary={cat.name}/>
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
};

export const StepFormDetails2 = ({
                                     wizard,
                                     setWizard,
                                 }) => (
    <Box
        sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
            p: 3
        }}
    >
        <Typography variant="h6" sx={{fontWeight: 600}}>
            Form Details
        </Typography>

        {wizard.details.formName && (
            <Typography
                variant="body2"
                sx={{color: 'text.secondary'}}
            >
                Form name: <strong>{wizard.details.formName}</strong>
            </Typography>
        )}

        {(wizard.form.type !== 'shareable' &&
            wizard.form.type !== 'application') && (
            <>
                <Typography
                    fontWeight="600"
                    variant="body1"
                    color="text.primary"
                    my={1}
                >
                    Would you like to set a reminder interval to remind the
                    recipient to fill in the form?
                </Typography>

                <TextField
                    sx={{
                        margin: 0,
                        flex: 2,
                        minWidth: '120px',
                    }}
                    label="Remind interval (optional)"
                    variant="outlined"
                    value={wizard.details.reminderFrequency}
                    onChange={(e) =>
                        setWizard((prev) => ({
                            ...prev,
                            details: {
                                ...prev.details,
                                reminderFrequency: e.target.value,
                            },
                        }))
                    }
                    margin="normal"
                    inputProps={{
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                    }}
                    helperText="In hours (0 = Disabled)"
                />
            </>
        )}

        <Typography
            fontWeight="600"
            variant="body1"
            color="text.primary"
            my={1}
        >
            Would you like to add an email address to notify when a form
            submission is made by a recipient? (optional)
        </Typography>

        <EmailsInput
            label="Enter Email address (optional)"
            values={wizard.details.notificationEmail.map((e) => ({
                email: e,
            }))}
            onChange={(items) =>
                setWizard((prev) => {
                    const seen = new Set();
                    const deduped = [];

                    for (const item of items || []) {
                        const raw = item?.email?.trim();
                        if (!raw) continue;

                        const key = raw.toLowerCase();
                        if (seen.has(key)) continue;

                        seen.add(key);
                        deduped.push(raw);
                    }

                    return {
                        ...prev,
                        details: {
                            ...prev.details,
                            notificationEmail: deduped,
                        },
                    };
                })
            }
            onBlur={(email) =>
                setWizard((prev) => {
                    if (!email) return prev;

                    const key = email.trim().toLowerCase();
                    const alreadyExists = prev.details.notificationEmail
                        .some((e) => e.trim().toLowerCase() === key);

                    if (alreadyExists) {
                        return prev;
                    }

                    return {
                        ...prev,
                        details: {
                            ...prev.details,
                            notificationEmail: [
                                ...prev.details.notificationEmail,
                                email.trim(),
                            ],
                        },
                    };
                })
            }
        />
    </Box>
);

export const StepFormDetails3 = ({
                                     wizard,
                                     setWizard,
                                 }) => {
    const onExpirationDateChange = (isoStringOrNull) => {
        setWizard((prev) => ({
            ...prev,
            details: {
                ...prev.details,
                expiryDate: isoStringOrNull,
            },
        }));
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 3,
                p: 3
            }}
        >
            <Typography variant="h6" sx={{fontWeight: 600}}>
                Form Details
            </Typography>

            {wizard.details.formName && (
                <Typography variant="body2" sx={{color: 'text.secondary'}}>
                    Form name: <strong>{wizard.details.formName}</strong>
                </Typography>
            )}

            <Typography variant="body1" color="text.primary" my={1}>
                <strong>Would you like to set an expiry date for the form? (optional)</strong>
                {(wizard.form.type === 'application' || wizard.form.type === 'shareable') &&
                    ' – expiry message will be displayed in place of form once expired.'}
            </Typography>

            <FormExpirationDatePicker
                label="Expiration Date (optional)"
                expirationDate={wizard.details.expiryDate || null}
                onExpirationDateChange={onExpirationDateChange}
            />
        </Box>
    );
};

export const StepFormEditor = ({
                                   wizard,
                                   setWizard,
                                   formId,
                                   accessLevel,
                                   onSchemaChange,
                                   schema,
                                   componentToEditIndex,
                                   setComponentToEditIndex,
                                   onSchemaValidationChange,
                                   scrollContainerRef,
                                   onScrollContainerScroll,
                               }) => {

    const selectedAudience = (() => {
        const raw = String(wizard.form?.selection ?? '');
        const [aud] = raw.split('|');
        const n = Number(aud);
        return Number.isFinite(n) ? n : (wizard.form?.audience ?? null);
    })();

    const triggerForm = wizard.form?.type === FORM_TYPE.triggerForm
        ? (wizard.trigger?.selectedForm ?? null)
        : null;

    const triggerAssignmentType = triggerForm?.assignmentType ?? null;
    const recipientsAssignmentType = wizard.recipients?.assignmentType ?? null;

    const effectiveAssignmentType = triggerAssignmentType ?? recipientsAssignmentType ?? null;

    const recipientType =
        selectedAudience === FORM_AUDIENCE_TYPE.externalUsers
            ? (effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.employees
                ? 'employee'
                : effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.students
                    ? 'student'
                    : null)
            : null;
    const linkedToStudents = !!wizard.details?.linkToStudents;
    const linkedToEmployees = !!wizard.details?.linkToEmployees;
    const parentAudienceType = (() => {
        const mappedParentAudienceType = getParentAudienceTypeForSelection(
            selectedAudience,
            wizard.details.parentAccountType
        );

        return (
            selectedAudience === FORM_AUDIENCE_TYPE.parentsForChildCompletion &&
            mappedParentAudienceType === PARENT_AUDIENCE_TYPE.none &&
            wizard.template.templateObject?.type === 'offer'
                ? PARENT_AUDIENCE_TYPE.applicant
                : mappedParentAudienceType
        );
    })();

    return (
        <FormEditor
            mode="edit"
            isWizardMode={true}
            audience={selectedAudience}
            parentAudienceType={parentAudienceType}
            recipientType={recipientType}
            triggerSourceAudience={triggerForm?.audience ?? null}
            linkedToStudents={linkedToStudents}
            linkedToEmployees={linkedToEmployees}
            wizardAllLinkedEmployeesCanSubmit={!!wizard.details?.allLinkedEmployeesCanSubmit}
            wizardLinkedStrictFiltering={!!wizard.details?.linkedStrictFiltering}
            wizardIsCommunicationForm={!!wizard.details?.isCommunication}
            wizardFormType={wizard.form?.type ?? null}
            wizardAccessLevel={accessLevel}
            wizard={wizard}
            setWizard={setWizard}
            formId={formId}
            forceFormId={formId}
            onSchemaChange={onSchemaChange}
            schema={schema}
            componentToEditIndex={componentToEditIndex}
            setComponentToEditIndex={setComponentToEditIndex}
            onSchemaValidationChange={onSchemaValidationChange}
            scrollContainerRef={scrollContainerRef}
            onScrollContainerScroll={onScrollContainerScroll}
        />
    );
};

// eslint-disable-next-line react/display-name
export const StepFormAccess = forwardRef(({
                                              wizard,
                                              setWizard,
                                              formId
                                          }, ref) => {
    return (
        <>
            <Box sx={{
                pt: 3,
                px: 3
            }}>
                <Typography fontWeight="600" variant="body1" color="text.primary" my={1}>
                    Which users and roles would you like to give access to view and manage this form?
                </Typography>
            </Box>
            <FormAccessSettings
                formId={formId}
                ref={ref}
                formName={wizard.details.formName}
                isFormCreation={false}
                wizardMode={true}
            />
        </>

    );
});

export const StepFormApprovals = ({formId, schema}) => {
    return (
        <>
            <Box sx={{
                pt: 3,
                px: 3
            }}>
                <Typography fontWeight="600" variant="body1" color="text.primary" my={1}>
                    Would you like to add approvals to this form?
                </Typography>
            </Box>
            <ApprovalSettings
                formId={formId}
                schema={schema}
                isFormLive={false}
                isFormPrivate={true}
            />
        </>
    );
};

export const StepAutoresponders = ({
                                       formId,
                                       wizard,
                                       setWizard
                                   }) => {
    const handleToggle = (value) => {
        setWizard(prev => ({
            ...prev,
            details: {
                ...prev.details,
                enableAutoresponder: value,
            },
        }));
    };

    const selectedAudience = (() => {
        const raw = String(wizard.form?.selection ?? '');
        const [aud] = raw.split('|');
        const n = Number(aud);
        return Number.isFinite(n) ? n : (wizard.form?.audience ?? null);
    })();

    const triggerForm = wizard.form?.type === FORM_TYPE.triggerForm
        ? (wizard.trigger?.selectedForm ?? null)
        : null;
    const triggerAssignmentType = triggerForm?.assignmentType ?? null;
    const recipientsAssignmentType = wizard.recipients?.assignmentType ?? null;

    const effectiveAssignmentType = triggerAssignmentType ?? recipientsAssignmentType ?? null;

    const recipientType =
        selectedAudience === FORM_AUDIENCE_TYPE.externalUsers
            ? (effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.employees
                ? 'employee'
                : effectiveAssignmentType === FORM_ASSIGNMENT_TYPE.students
                    ? 'student'
                    : null)
            : null;

    return (
        <>
            <Box sx={{
                pt: 3,
                px: 3
            }}>
                <Typography fontWeight="600" variant="body1" color="text.primary" my={1}>
                    Would you like to add automatic response emails to this form?
                </Typography>
            </Box>
            <AutoresponderSettings
                formId={formId}
                schema={wizard.schema}
                accessLevel={wizard.details.accessLevel}
                enableAutoresponder={wizard.details.enableAutoresponder}
                onAutoresponderToggle={handleToggle}
                audience={selectedAudience}
                recipientType={recipientType}
                linkedToStudents={!!wizard.details?.linkToStudents}
                isFormArchived={false}
            />
        </>
    );
};

export const StepGoLive = ({
                               wizard,
                               formId,
                               hasRecipients,
                               onMakeLive,
                               onSchedule,
                               onLater,
                               formName,
                               mode,
                               isPublishing = false
                           }) => {
    const [date, setDate] = useState(null);
    const type = wizard.form?.type;
    const navigate = useNavigate();

    const isPublicForm = type === 'shareable' || type === 'application';

    const formUrl = useMemo(
        () => (formId ? getFormUrl(formId) : ''),
        [formId]
    );

    const iframe = useMemo(
        () => (formId ? getIFrame(formId) : ''),
        [formId]
    );

    const {
        showErrorNotification,
        showSuccessNotification,
        showDetailedErrorNotification
    } = useNotification();

    const copyToClipBoard = async (src, label) => {
        if (!src) return;

        try {
            await navigator.clipboard.writeText(src);
            showSuccessNotification?.(`${label} copied to clipboard`);
        } catch (e) {
            if (showDetailedErrorNotification) {
                showDetailedErrorNotification(
                    'Unable to copy to clipboard',
                    e?.message || 'Please copy the value manually.'
                );
            } else if (showErrorNotification) {
                showErrorNotification('Unable to copy to clipboard');
            }
        }
    };

    const canShowMakeLiveNow =
        hasRecipients ||
        type === 'shareable' ||
        type === 'application' ||
        type === 'triggerForm';

    const handleMakeLiveNow = () => {
        onMakeLive(formId, formName);
    };

    const handleGoToForm = () => {
        if (formId) {
            navigate(`/forms/edit/${formId}`);
        } else {
            showErrorNotification?.('Form not found');
        }
    };

    const headingText =
        mode === 'template'
            ? 'Would you like to schedule a date to go live?'
            : 'When would you like this form to go live?';

    return (
        <Box sx={{p: 3}}>
            <Typography fontWeight="600" variant="body1" color="text.primary" mb={3}>
                {headingText}
            </Typography>

            {mode !== 'template' &&
                !hasRecipients &&
                type !== 'shareable' &&
                type !== 'application' &&
                type !== 'triggerForm' &&
                (
                    <Typography variant="body2" color="error" mb={2}>
                        This form has no recipients. You cannot make it live until at least one
                        recipient is added. You can still schedule a future live date.
                    </Typography>
                )}

            <Stack spacing={2}>
                <Stack
                    direction={{
                        xs: 'column',
                        sm: 'row'
                    }}
                    spacing={1.5}
                    alignItems={{
                        xs: 'stretch',
                        sm: 'center'
                    }}
                >
                    {canShowMakeLiveNow && (
                        <Button
                            variant="contained"
                            disabled={isPublishing}
                            onClick={handleMakeLiveNow}
                            sx={{
                                backgroundColor: '#186BBE',
                                boxShadow: 'none',
                                width: {
                                    xs: '100%',
                                    sm: '250px'
                                },
                                '&:hover': {
                                    backgroundColor: '#155FA7',
                                    boxShadow: 'none',
                                },
                            }}
                        >
                            Make Live Now
                        </Button>
                    )}
                    <Button
                        variant="filled"
                        onClick={handleGoToForm}
                        sx={{
                            width: {
                                xs: '100%',
                                sm: '250px'
                            },
                            color: '#186BBE',
                            backgroundColor: '#f0f5fa',
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#f0f5fa',
                                boxShadow: 'none',
                            },
                        }}
                    >
                        Go to form
                    </Button>
                    <Button
                        variant="outlined"
                        disabled={isPublishing}
                        onClick={onLater}
                        sx={{
                            width: {
                                xs: '100%',
                                sm: '250px'
                            },
                        }}
                    >
                        Make Live Later
                    </Button>

                </Stack>

                <Box>
                    <Divider sx={{my: 2}}/>
                </Box>

                <Box
                    sx={{
                        p: 2,
                        border: '1px solid #ddd',
                        borderRadius: 2
                    }}
                >
                    <Typography variant="subtitle1" mb={1}>
                        Schedule a future live date
                    </Typography>

                    <DateTimePicker
                        label="Activation date"
                        value={date}
                        onChange={setDate}
                        minDateTime={moment()}
                        ampm={false}
                        slotProps={{textField: {fullWidth: true}}}
                    />

                    <Button
                        variant="outlined"
                        disabled={!date || isPublishing}
                        onClick={() => onSchedule(formId, date.toISOString())}
                        sx={{
                            mt: 2,
                            width: {
                                xs: '100%',
                                sm: '250px'
                            },
                        }}
                    >
                        Schedule Live Date
                    </Button>
                </Box>

                {isPublicForm && formId && (
                    <>
                        <Box>
                            <Divider sx={{my: 2}}/>
                        </Box>
                        <Box>
                            <Typography variant="subtitle1" fontWeight={600} gutterBottom mb={3}>
                                Copy the link or embed code below to share the live form on your website or in
                                communications:
                            </Typography>

                            <Box mb={3}>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Public link
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={1.5}>
                                    Share this URL directly with people who need to complete the form.
                                </Typography>
                                <Stack
                                    direction={{
                                        xs: 'column',
                                        sm: 'row'
                                    }}
                                    alignItems={{
                                        xs: 'stretch',
                                        sm: 'center'
                                    }}
                                    spacing={1.5}
                                >
                                    <TextField
                                        fullWidth
                                        value={formUrl}
                                        label="Form URL"
                                        variant="outlined"
                                        InputProps={{readOnly: true}}
                                        size="small"
                                    />
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            onClick={() => copyToClipBoard(formUrl, 'URL')}
                                            sx={{
                                                alignSelf: {
                                                    xs: 'flex-end',
                                                    sm: 'center'
                                                },
                                                mt: {
                                                    xs: 0.5,
                                                    sm: 0
                                                }
                                            }}
                                        >
                                            <ContentCopyRoundedIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>

                            <Box>
                                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                                    Embed on your website
                                </Typography>
                                <Typography variant="body2" color="text.secondary" mb={1.5}>
                                    Use this embed code to display the form inside another website.
                                </Typography>
                                <Stack
                                    direction={{
                                        xs: 'column',
                                        sm: 'row'
                                    }}
                                    alignItems={{
                                        xs: 'stretch',
                                        sm: 'flex-start'
                                    }}
                                    spacing={1.5}
                                >
                                    <TextField
                                        fullWidth
                                        multiline
                                        minRows={3}
                                        maxRows={6}
                                        value={iframe}
                                        label="Embed code"
                                        variant="outlined"
                                        InputProps={{
                                            readOnly: true,
                                            sx: {
                                                fontFamily:
                                                    'Monaco, Menlo, Consolas, "Courier New", monospace',
                                                fontSize: 12
                                            }
                                        }}
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                        sx={{
                                            '& .MuiInputLabel-outlined': {
                                                backgroundColor: '#fff',
                                                paddingRight: 1,
                                            },
                                        }}
                                    />
                                    <Tooltip title="Copy to clipboard">
                                        <IconButton
                                            onClick={() => copyToClipBoard(iframe, 'Embed code')}
                                            sx={{
                                                alignSelf: {
                                                    xs: 'flex-end',
                                                    sm: 'flex-start'
                                                },
                                                mt: {
                                                    xs: 0.5,
                                                    sm: 0
                                                }
                                            }}
                                        >
                                            <ContentCopyRoundedIcon/>
                                        </IconButton>
                                    </Tooltip>
                                </Stack>
                            </Box>
                        </Box>
                    </>
                )}
            </Stack>
        </Box>
    );
};
